import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatStamp } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

/** Set or change your own password. The current one is asked once you have one (unless the owner reset it). */
export function PasswordPage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const needCurrent = !!me?.hasPassword && !me.mustChange;
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const save = useMutation({
    mutationFn: () => api.changePassword({ ...(needCurrent ? { current } : {}), password }),
    onSuccess: () => {
      setCurrent('');
      setPassword('');
      setRepeat('');
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return setProblem('passwordRule');
    if (password !== repeat) return setProblem('passwordsDiffer');
    setProblem(null);
    save.mutate();
  };
  const wrongCurrent = save.error instanceof ApiError && save.error.status === 400;
  return (
    <div className="stack">
      <PageHead title={me?.hasPassword ? t('changePassword') : t('setPassword')} back="/" />
      {me?.mustChange ? <p className="note">{t('mustChangeHint')}</p> : null}
      <form className="stack card" onSubmit={submit} noValidate data-testid="password-form">
        {needCurrent ? (
          <label className="field">
            <span>{t('currentPassword')}</span>
            <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </label>
        ) : null}
        <label className="field">
          <span>{t('newPassword')}</span>
          <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('repeatPassword')}</span>
          <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </label>
        <p className="muted">{t('passwordRule')}</p>
        {problem ? <p className="field-error" role="alert">{t(problem)}</p> : null}
        {wrongCurrent ? <p className="field-error" role="alert">{t('wrongLogin')}</p> : save.error ? <ErrorBox error={save.error} /> : null}
        {save.isSuccess ? <p className="note" role="status">{t('passwordChanged')}</p> : null}
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={save.isPending}>
          {t('save')}
        </button>
      </form>
      <Passkeys />
    </div>
  );
}

/** The phones this person signs in with by face or fingerprint; add this one, forget a lost one. */
function Passkeys() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: qk.passkeys, queryFn: ({ signal }) => api.passkeys(signal) });
  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.passkeys });
  const add = useMutation({
    mutationFn: async () => {
      const { challengeId, options } = await api.passkeyRegisterOptions();
      const response = await startRegistration({ optionsJSON: options as unknown as Parameters<typeof startRegistration>[0]['optionsJSON'] });
      return api.passkeyRegisterVerify(challengeId, response as unknown as Record<string, unknown>);
    },
    onSuccess: invalidate,
  });
  const forget = useMutation({ mutationFn: (id: string) => api.forgetPasskey(id), onSuccess: invalidate });
  const supported = typeof window !== 'undefined' && browserSupportsWebAuthn();
  const cancelled = add.error instanceof DOMException && add.error.name === 'NotAllowedError';
  return (
    <section className="card stack-sm" data-testid="passkeys">
      <h2>{t('passkeysTitle')}</h2>
      <p className="muted">{t('passkeysHint')}</p>
      <QueryView query={list}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noPasskeys')}</Empty>
          ) : (
            <ul className="list plain">
              {rows.map((p) => (
                <li key={p.id} className="row">
                  <span className="row-main">
                    <span className="row-title">{p.deviceLabel ?? t('phone')}</span>
                    <span className="row-sub">
                      {t('added')} {formatStamp(p.createdAt, intlLocale)}
                      {p.lastUsedAt ? ` · ${t('lastSeen')} ${formatStamp(p.lastUsedAt, intlLocale)}` : ''}
                    </span>
                  </span>
                  <span className="row-end">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={forget.isPending} onClick={() => { if (window.confirm(`${t('forget')} ${p.deviceLabel ?? t('phone')}: ${t('confirmSure')}`)) forget.mutate(p.id); }} aria-label={`${t('forget')} ${p.deviceLabel ?? t('phone')}`}>
                      {t('forget')}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
      {add.isError && !cancelled ? <ErrorBox error={add.error} /> : null}
      {supported ? (
        <button type="button" className="btn btn-primary btn-block" disabled={add.isPending} onClick={() => add.mutate()}>
          {t('addThisPhone')}
        </button>
      ) : (
        <p className="muted">{t('passkeysUnsupported')}</p>
      )}
    </section>
  );
}
